import type { Incident } from '../../services/incidentsApi';
import { readFollowupOwnerLabel } from '../../domain/incidents/incidentOwnershipSemantics';

const REVIEW_OUTCOME_LABELS: Record<string, string> = {
  true_event: 'True event',
  false_positive: 'False positive',
  demo_or_test_activity: 'Special-case activity',
  expected_activity: 'Expected activity',
  duplicate: 'Duplicate',
  not_actionable: 'Not actionable',
  insufficient_evidence: 'Insufficient evidence',
};

function safeText(value: unknown): string {
  return String(value || '').trim();
}

export function incidentCanonicalReviewOutcome(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText((incident as any)?.safety_case?.review?.review_outcome).toLowerCase();
}

export function incidentCanonicalReviewOutcomeLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  const outcome = incidentCanonicalReviewOutcome(incident);
  if (!outcome) return '';
  return REVIEW_OUTCOME_LABELS[outcome] || outcome.replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export function incidentCanonicalOperatorNote(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText(
    (incident as any)?.safety_case?.review?.operator_note
    || (incident as any)?.operator_note,
  );
}

export function incidentCanonicalEvidenceReadinessState(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText(
    (incident as any)?.safety_case?.evidence?.readiness_state,
  ).toUpperCase();
}

export function incidentCanonicalCameraCalibrationStatus(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText(
    (incident as any)?.safety_case?.evidence_quality?.camera_calibration_status
    || (incident as any)?.safety_case?.calibration_artifact?.camera_calibration_status
    || (incident as any)?.metrics?.camera_calibration_status,
  ).toLowerCase();
}

export function incidentCanonicalCameraCalibrationLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  const status = incidentCanonicalCameraCalibrationStatus(incident);
  if (status === 'floor_plane_calibrated') return 'Floor-plane calibrated';
  if (status === 'frame_normalized_only') return 'Camera view only';
  if (status === 'calibration_stale') return 'Calibration stale';
  if (status === 'not_calibrated') return 'Not calibrated';
  return status ? status.replace(/_/g, ' ') : '';
}

export function incidentCanonicalEvidenceQualityLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  const quality = safeText((incident as any)?.safety_case?.evidence_quality?.quality).toLowerCase();
  if (!quality) return '';
  return quality.charAt(0).toUpperCase() + quality.slice(1);
}

export function incidentCanonicalMeasurementBasisLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  const basis = safeText((incident as any)?.safety_case?.evidence_quality?.measurement_basis).toLowerCase();
  if (!basis) return '';
  return basis.replace(/_/g, ' ');
}

export function incidentCanonicalFollowupOwnerLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  const fromOps = readFollowupOwnerLabel(incident);
  if (fromOps) return fromOps;
  return safeText(
    (incident as any)?.safety_case?.ownership?.followup_owner
    || (incident as any)?.safety_case?.action_snapshot?.owner
    || (incident as any)?.safety_case?.ownership?.assignee
    || (incident as any)?.safety_case?.ownership?.assignment_team
    || (incident as any)?.assignment_team
    || (incident as any)?.assignmentTeam,
  );
}

export function incidentCanonicalRuleOwnerLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText(
    (incident as any)?.safety_case?.ownership?.rule_owner
    || (incident as any)?.taxonomy?.owner
    || (incident as any)?.owner,
  );
}

export function incidentCanonicalOwnerLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return incidentCanonicalFollowupOwnerLabel(incident);
}

export function incidentCanonicalFollowUpStateLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  const resolvedBy = safeText(
    (incident as any)?.safety_case?.ownership?.resolved_by
    || (incident as any)?.resolved_by,
  );
  const resolvedAt = safeText(
    (incident as any)?.safety_case?.ownership?.resolved_at
    || (incident as any)?.resolved_at,
  );
  const workflowStatus = safeText(
    (incident as any)?.safety_case?.review?.workflow_status
    || (incident as any)?.safety_case?.lifecycle?.workflow_status
    || (incident as any)?.status,
  ).toUpperCase();

  if (resolvedBy || resolvedAt || workflowStatus === 'CLOSED') {
    return resolvedBy ? `Review closed by ${resolvedBy}` : 'Review closed';
  }

  if (incidentCanonicalFollowupOwnerLabel(incident)) {
    return 'Action owner assigned, follow-up pending';
  }

  return '';
}

export function incidentCanonicalEscalationState(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText(
    (incident as any)?.safety_case?.escalation?.state,
  ).toLowerCase();
}

export function incidentCanonicalEscalationReasons(
  incident: Incident | Record<string, any> | null | undefined,
): string[] {
  const canonical = (incident as any)?.safety_case?.escalation?.reasons;
  if (Array.isArray(canonical)) {
    return canonical.map((value: unknown) => safeText(value)).filter(Boolean);
  }
  const legacy = (incident as any)?.ops_escalation_reasons;
  if (Array.isArray(legacy)) {
    return legacy.map((value: unknown) => safeText(value)).filter(Boolean);
  }
  return [];
}

export function incidentCanonicalNextReviewAt(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText(
    (incident as any)?.safety_case?.escalation?.next_review_at
    || (incident as any)?.safety_case?.action_snapshot?.next_review_at
    || (incident as any)?.sla_target_at,
  );
}

export function incidentCanonicalEvidenceReadinessLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  const explicitLabel = safeText(
    (incident as any)?.safety_case?.evidence?.readiness_label
    || (incident as any)?.safety_case?.evidence?.evidence_readiness_label,
  );
  if (explicitLabel) return explicitLabel;
  const state = incidentCanonicalEvidenceReadinessState(incident);
  const missingHighRiskClips = Number(
    (incident as any)?.safety_case?.evidence?.missing_high_risk_clips
    ?? (incident as any)?.missing_high_risk_clips
    ?? 0,
  );
  const evidenceOperatorStatus = safeText(
    (incident as any)?.safety_case?.evidence?.evidence_operator_status
    || (incident as any)?.evidence_operator_status,
  ).toUpperCase();
  if (!state) return '';
  if (
    state === 'READY'
    && (
      missingHighRiskClips > 0
      || ['FAILING', 'FAILED', 'NOT_READY', 'DEGRADED'].includes(evidenceOperatorStatus)
    )
  ) {
    return 'Ready with limitations';
  }
  if (state === 'READY') return 'Ready';
  if (state === 'DEGRADED') return 'Degraded';
  if (state === 'FAILED') return 'Failed';
  if (state === 'PENDING') return 'Pending';
  return state.replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export function incidentCanonicalSourceContextLabel(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  const sourceType = safeText(
    (incident as any)?.safety_case?.evidence?.source_type
    || (incident as any)?.safety_case?.evidence?.source_context
    || (incident as any)?.safety_case?.source_context?.source_type
    || (incident as any)?.safety_case?.source_context?.source_context,
  );
  if (!sourceType) return '';
  return sourceType.replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export function incidentCanonicalProofScopeId(
  incident: Incident | Record<string, any> | null | undefined,
): string {
  return safeText(
    (incident as any)?.proof_scope?.proof_scope_id
    || (incident as any)?.safety_case?.traceability?.proof_scope_id
    || (incident as any)?.safety_case?.proof_scope?.proof_scope_id,
  );
}
